import { useState, useRef } from "react";
import { Upload, X, Plus, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MultiImageUploader = ({ 
  values = [], 
  onChange, 
  label = "Images",
  maxImages = 20,
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getFullUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max images
    if (values.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images autorisées`);
      return;
    }

    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(f => !validTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      toast.error("Certains fichiers ne sont pas des images valides (JPG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error("Certains fichiers dépassent 10MB");
      return;
    }

    setUploading(true);
    const newUrls = [...values];

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const token = localStorage.getItem("admin_token");
        const response = await axios.post(`${API}/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${token}`
          }
        });
        newUrls.push(response.data.url);
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    onChange(newUrls);
    setUploading(false);
    toast.success(`${files.length} image(s) uploadée(s)`);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = (index) => {
    const newUrls = values.filter((_, i) => i !== index);
    onChange(newUrls);
  };

  const handleAddUrl = () => {
    const url = prompt("Entrez l'URL de l'image:");
    if (url && url.trim()) {
      if (values.length >= maxImages) {
        toast.error(`Maximum ${maxImages} images autorisées`);
        return;
      }
      onChange([...values, url.trim()]);
    }
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= values.length) return;
    const newUrls = [...values];
    const [moved] = newUrls.splice(fromIndex, 1);
    newUrls.splice(toIndex, 0, moved);
    onChange(newUrls);
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          {label} ({values.length}/{maxImages})
        </label>
        <button
          type="button"
          onClick={handleAddUrl}
          className="text-xs text-yellow-600 hover:text-yellow-700"
        >
          + Ajouter URL
        </button>
      </div>
      
      {/* Images Grid */}
      {values.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {values.map((url, index) => (
            <div 
              key={index} 
              className="relative group aspect-video bg-gray-100 rounded-lg overflow-hidden"
            >
              <img 
                src={getFullUrl(url)} 
                alt={`Image ${index + 1}`} 
                className="w-full h-full object-cover"
              />
              {/* Overlay with controls */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => moveImage(index, index - 1)}
                    className="p-1.5 bg-white/90 rounded-full hover:bg-white text-gray-700"
                    title="Déplacer avant"
                  >
                    ←
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="p-1.5 bg-red-500 rounded-full hover:bg-red-600 text-white"
                  title="Supprimer"
                >
                  <X className="w-4 h-4" />
                </button>
                {index < values.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveImage(index, index + 1)}
                    className="p-1.5 bg-white/90 rounded-full hover:bg-white text-gray-700"
                    title="Déplacer après"
                  >
                    →
                  </button>
                )}
              </div>
              {/* Position badge */}
              {index === 0 && (
                <span className="absolute top-1 left-1 bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      <div 
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          uploading 
            ? 'border-yellow-400 bg-yellow-50' 
            : 'border-gray-300 hover:border-yellow-400 hover:bg-yellow-50'
        }`}
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mb-2" />
            <p className="text-gray-500 text-sm">Upload en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-2 mb-1">
              <Upload className="w-6 h-6 text-gray-400" />
              <Plus className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-gray-600 text-sm font-medium">Ajouter des photos</p>
            <p className="text-gray-400 text-xs mt-1">JPG, PNG, GIF, WEBP (max 10MB/image)</p>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        multiple
        className="hidden"
      />

      <p className="text-xs text-gray-400 mt-2">
        💡 La première image sera l'image principale. Glissez pour réorganiser.
      </p>
    </div>
  );
};

export default MultiImageUploader;
